import { EmailIcon } from '@chakra-ui/icons'
import { Flex, Popover, PopoverBody, PopoverContent, PopoverTrigger, Text } from '@chakra-ui/react'
import { AppContext, ScreenContext, usePopUpMessage } from '@hb/react'
import { ErrorWithMessage, getDateTimeString, GynInviteEmailData } from '@hb/shared'
import { useCallback, useContext, useMemo, useState } from 'react'
import { sendGynInviteEmail } from '../../../../backend/functions/gyn'
import { practiceSelectField } from '../../../../collections/fields/practice'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useSelectedPractice } from '../../../../hooks'
import { ActionLog } from '../../../ActionLog'
import { ActionButton, SolidActionButton } from '../../../Buttons'
import { StandaloneInput } from '../../../forms/Input'
import { Subheader } from '../../Text'

const GynInviteDataView = ({ inviteEmail }: { inviteEmail: GynInviteEmailData }) => (
  <Flex>
    <ActionLog action="sent" by={inviteEmail.sentBy} on={inviteEmail.sentOn} />
  </Flex>
)

const GynEmailIcon = ({ color }: { color: string }) => (
  <EmailIcon
    filter={color === 'white' ? 'drop-shadow(1px 1px 2px #00000077)' : undefined}
    color={color}
  />
)

export const GynInvitePopover = () => {
  const { user, userId } = useProfile()
  const { inviteEmail } = user?.gyn ?? {}
  const { showError, showSuccess } = usePopUpMessage()
  const { appName } = useContext(AppContext)
  const { id: providerPracticeId } = useSelectedPractice()
  const [adminPracticeId, setAdminPracticeId] = useState<string | null>(
    user?.gyn?.corrections?.practiceId ?? user?.gyn?.data?.practiceId ?? null,
  )
  const practiceId = useMemo(() => {
    if (appName === 'providers-app') return providerPracticeId
    return adminPracticeId
  }, [appName, providerPracticeId, adminPracticeId])

  const { isMobile } = useContext(ScreenContext)
  const [isSending, setIsSending] = useState(false)

  const handleSendInviteEmail = useCallback(async () => {
    if (!userId) return
    if (!user?.fname) {
      showError('Patient first name is not set')
      return
    }
    if (!user?.email) {
      showError('Patient email is not set')
      return
    }
    if (!practiceId) {
      showError('Please select a practice')
      return
    }
    try {
      setIsSending(true)
      await sendGynInviteEmail({
        patientId: userId,
        practiceId,
      })
      showSuccess(
        `GYN Invite email sent to ${user?.nickname || user?.fname}`,
        `Email sent to ${user?.email}`,
      )
    } catch (error) {
      showError('Error sending GYN invite', (error as ErrorWithMessage).message ?? 'Unknown error')
    } finally {
      setIsSending(false)
    }
  }, [userId, user, showError, showSuccess, practiceId])

  const showPopover = !(!inviteEmail && appName === 'providers-app')
  const SendButtonComponent = showPopover ? SolidActionButton : ActionButton
  const sendButton = (
    <SendButtonComponent
      isDisabled={!practiceId}
      isLoading={isSending}
      w={!inviteEmail && appName === 'providers-app' ? 'auto' : '100%'}
      size={isMobile ? 'xs' : 'sm'}
      colorScheme={showPopover ? 'green' : 'gray'}
      gap={2}
      onClick={handleSendInviteEmail}>
      <GynEmailIcon color={showPopover ? 'white' : 'gray.500'} />
      <Text>Send{inviteEmail ? ' another' : ''} GYN Invite</Text>
    </SendButtonComponent>
  )
  if (!inviteEmail && appName === 'providers-app') return sendButton
  return (
    <Popover isLazy trigger="hover" closeOnBlur={false} strategy="fixed" placement="bottom-end">
      <PopoverTrigger>
        {inviteEmail ? (
          <ActionButton colorScheme="gray" size={isMobile ? 'xs' : 'sm'} gap={2}>
            <GynEmailIcon color="gray.500" />
            <Text>GYN Invite Sent {getDateTimeString(inviteEmail.sentOn, 'short')}</Text>
          </ActionButton>
        ) : (
          <ActionButton colorScheme="gray" size={isMobile ? 'xs' : 'sm'} gap={2}>
            <GynEmailIcon color="gray.500" />
            <Text>Send GYN Invite</Text>
          </ActionButton>
        )}
      </PopoverTrigger>
      <PopoverContent>
        <PopoverBody>
          {inviteEmail ? (
            <>
              <GynInviteDataView inviteEmail={inviteEmail} />
              {inviteEmail.previousInvites?.map(previousInvite => (
                <GynInviteDataView
                  key={getDateTimeString(previousInvite.sentOn, 'short')}
                  inviteEmail={previousInvite}
                />
              ))}
            </>
          ) : null}
          {appName === 'app' ? (
            <Flex flexFlow="column" gap={1} pb={2}>
              <Subheader fontWeight={500}>GYN Care Provider</Subheader>
              <StandaloneInput
                theme="detailed"
                value={adminPracticeId}
                field={practiceSelectField}
                onChange={setAdminPracticeId}
              />
            </Flex>
          ) : null}
          {sendButton}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
